/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type SessionService from '@linc-technologies/linced-common/services/session';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import ReturnMonth from 'linced-app/enums/nz-moe-roll-return-month';
import type ReturnDate from 'linced-app/models/nz-roll-returns/return-date';
import type ValidationResult from 'linced-app/models/nz-roll-returns/validation-result';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';
import type RollReturnDateService from 'linced-app/services/roll-return-date';

// Proto: nz_moe_roll_returns.RollReturn.
export default class RollReturn extends Model {
  declare [Type]: 'nz-roll-returns/roll-return';

  @service
  declare session: SessionService;

  @service
  declare rollReturnDate: RollReturnDateService;

  @attr('number')
  declare createTime: number | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @attr('number', { defaultValue: 0 })
  declare rollReturnYear: number;

  @attr('string', { defaultValue: '' })
  declare rollReturnMonth: string;

  @computed('rollReturnMonth')
  get fullMonth() {
    const label = ReturnMonth.getLabel(this.rollReturnMonth);
    if (isEmpty(label)) {
      return '';
    }
    return label;
  }

  @computed('rollReturnMonth')
  get monthIndex() {
    const value = findBy(ReturnMonth.values, 'value', this.rollReturnMonth);
    if (isEmpty(value)) {
      return -1;
    }
    return ReturnMonth.values.indexOf(value!);
  }

  @attr('boolean', { defaultValue: false })
  declare isDraft: boolean;

  // TS-BUG: this replaces Model.isValid, so the record state no longer shows
  // server validation errors (see HE-161).
  @attr('boolean', { defaultValue: false })
  // @ts-expect-error The attr replaces the Model.isValid accessor.
  declare isValid: boolean;

  @belongsTo('person', { async: true, inverse: null })
  declare approverId: AsyncBelongsTo<Person>;

  @alias('approverId')
  declare approver: AsyncBelongsTo<Person>;

  @attr('number', { defaultValue: 0 })
  declare approveTime: number;

  @computed('approveTime')
  get isApproved() {
    return Boolean(this.approveTime);
  }

  @attr('string', { defaultValue: '' })
  declare donationScheme: string;

  @attr('boolean', { defaultValue: false })
  declare validateOnly: boolean;

  @attr('string', { defaultValue: '' })
  declare moeFileData: string;

  @belongsTo('person', { async: true, inverse: null })
  declare uploaderId: AsyncBelongsTo<Person>;

  @alias('uploaderId')
  declare uploader: AsyncBelongsTo<Person>;

  @attr('number', { defaultValue: 0 })
  declare uploadTime: number;

  @computed('uploadTime')
  get isUploaded() {
    return Boolean(this.uploadTime);
  }

  @belongsTo('person', { async: true, inverse: null })
  declare rejecterId: AsyncBelongsTo<Person>;

  @alias('rejecterId')
  declare rejecter: AsyncBelongsTo<Person>;

  @attr('number', { defaultValue: 0 })
  declare rejectTime: number;

  @computed('rejectTime')
  get isRejected() {
    return Boolean(this.rejectTime);
  }

  @hasMany('nz-roll-returns/validation-result', {
    async: true,
    inverse: 'rollReturnId',
  })
  declare validationResults: AsyncHasMany<ValidationResult>;

  @computed(
    'session.school.id',
    'rollReturnDate.currentReturnDate.{code,year}',
    'rollReturnYear',
    'rollReturnMonth',
  )
  get isCurrentRollReturn() {
    // For our Roll Return Test School (DEV+STAGING) make all returns current
    if (
      this.get('session.school.id') === '5250917d-7b78-4295-8306-72097571f43d'
    ) {
      return true;
    }
    if (isEmpty(this.rollReturnDate.currentReturnDate)) {
      return false;
    }
    // TS-STANDIN(roll-return-date): service is JS, so currentReturnDate infers
    // as null.
    const currentReturnDate = this.rollReturnDate
      .currentReturnDate as unknown as ReturnDate;
    const isCurrentRRMonth = this.rollReturnMonth === currentReturnDate.code;
    const isCurrentRRYear = this.rollReturnYear === currentReturnDate.year;
    return isCurrentRRMonth && isCurrentRRYear;
  }

  @computed(
    'isDraft',
    'isValid',
    'isApproved',
    'isRejected',
    'isUploaded',
    'validateOnly',
    'isCurrentRollReturn',
  )
  get status() {
    let status = '';
    switch (true) {
      case !this.isCurrentRollReturn:
        status = 'Submission cut-off date is passed';
        break;
      case !this.isValid:
        status = 'Not valid';
        break;
      case this.validateOnly:
        status = 'Valid';
        break;
      case this.isDraft:
        status = 'Draft';
        break;
      case this.isApproved && this.isUploaded:
        status = 'Approved and uploaded';
        break;
      case this.isApproved:
        status = 'Approved (pending upload)';
        break;
      case this.isRejected:
        status = 'Rejected';
        break;
      default:
        status = 'Pending approval';
        break;
    }
    return status;
  }

  @computed('status')
  get statusIcon() {
    switch (this.status) {
      case 'Pending approval':
      case 'Approved (pending upload)':
        return 'warning';
    }
    return '';
  }
}
